import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { IBasicTemporalPass, IFare, ITemporalPass } from 'src/app/models';
import { TemporalType, UserRole } from 'src/app/models/enums';
import { LiteralService } from 'src/app/services/literal/literal.service';
import { TemporalPassesService } from 'src/app/services/temporal-passes/temporal-passes.service';
import { images } from 'src/images';

@Component({
  selector: 'app-temporal-passes-tab',
  templateUrl: './temporal-passes-tab.component.html',
  styleUrl: './temporal-passes-tab.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class TemporalPassesTabComponent implements OnChanges {
  @Input() temporalPasses: IBasicTemporalPass[] = [];
  @Input() selectedTemporalPass: IBasicTemporalPass | undefined;
  @Input() fares: IFare[] = [];
  @Input() clientId: number;
  @Input() userRole: UserRole;
  @Input() hideFooter = false;
  @Output() action = new EventEmitter<any>();
  @Output() deleteEvent = new EventEmitter<IBasicTemporalPass>();

  public images = images;
  public UserRole = UserRole;
  public TemporalType = TemporalType;

  public isCreating = false;
  public isEditing = false;

  public editingPass: ITemporalPass = this.getEmpty();

  constructor(
    public literalService: LiteralService,
    private temporalPassesService: TemporalPassesService,
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (
      (changes['selectedTemporalPass'] || changes['temporalPasses']) &&
      this.selectedTemporalPass?.id
    ) {
      this.loadFullPass(this.selectedTemporalPass.id);
    }
  }

  private async loadFullPass(id: number): Promise<void> {
    try {
      const full = await this.temporalPassesService.getTemporalPass(id);
      this.editingPass = { ...full };
    } catch {
      const basic = this.temporalPasses.find((p) => p.id === id);
      this.editingPass = { ...(basic as IBasicTemporalPass), fares: [] };
    }
  }

  getEmpty(): ITemporalPass {
    return {
      id: undefined,
      name: '',
      price: 0,
      client: undefined,
      type: TemporalType.Monthly,
      usageLimit: 0,
      roundToNaturalPeriod: false,
      fares: [],
    };
  }

  getTemporalTypes(): any[] {
    return Object.values(TemporalType).map((t) => ({
      id: t,
      value: t,
      label: this.literalService.get('temporalType.' + t, true),
    }));
  }

  getPassesForSelect(): any[] {
    const suffix =
      ' (' + this.literalService.get('services.routes.deactivated', true) + ')';
    return this.temporalPasses.map((p) => ({
      id: p.id,
      value: p.id,
      label: p.active === false ? `${p.name}${suffix}` : p.name,
    }));
  }

  isCurrentDeactivated(): boolean {
    return !!this.editingPass.id && this.editingPass.active === false;
  }

  getFaresForSelect(): any[] {
    return this.fares.map((f) => ({ id: f.id, value: f.id, label: f.name }));
  }

  getSelectedFareIds(): number[] {
    return (this.editingPass.fares || [])
      .map((f) => f.id)
      .filter((id): id is number => id !== undefined);
  }

  getSelectedFaresLabel(): string {
    return (this.editingPass.fares || []).map((f) => f.name ?? '').join(', ');
  }

  startCreating() {
    this.isCreating = true;
    this.isEditing = false;
    this.editingPass = this.getEmpty();
  }

  startEditing() {
    if (this.editingPass.id) {
      this.isEditing = true;
      this.isCreating = false;
    }
  }

  cancelEdit() {
    this.isCreating = false;
    this.isEditing = false;
    if (this.editingPass.id) {
      this.loadFullPass(this.editingPass.id);
    } else {
      this.editingPass = this.getEmpty();
    }
  }

  async selectPass(event: any) {
    const selectedId = event.options
      ? event.options[0]?.id
      : event.target?.value;
    if (selectedId) {
      await this.loadFullPass(selectedId);
      this.isCreating = false;
      this.isEditing = false;
    }
  }

  changeType(event: any) {
    const typeValue = event.options
      ? event.options[0]?.value
      : event.target?.value;
    this.editingPass.type = typeValue as TemporalType;
  }

  changeFares(event: any) {
    const selectedIds: number[] = event.options
      ? event.options.map((o: any) => o.id)
      : Array.isArray(event.value)
        ? event.value
        : [];
    this.editingPass.fares = selectedIds
      .map((id) => this.fares.find((f) => f.id === id))
      .filter((f): f is IFare => !!f)
      .map((f) => ({ id: f.id, name: f.name }));
  }

  updateName(event: any) {
    this.editingPass.name = event.target?.value ?? event;
  }

  updatePrice(event: any) {
    const value = event.target?.value ?? event;
    this.editingPass.price = value ? parseFloat(value) : 0;
  }

  updateUsageLimit(event: any) {
    const value = event.target?.value ?? event;
    this.editingPass.usageLimit = value ? parseInt(value, 10) : 0;
  }

  toggleRoundToNaturalPeriod(event: any) {
    this.editingPass.roundToNaturalPeriod = !!event.target?.checked;
  }

  onDelete() {
    if (this.editingPass.id) {
      this.deleteEvent.emit(this.editingPass);
    }
  }

  onSubmit() {
    this.action.emit({
      temporalPass: {
        ...this.editingPass,
        clientId: this.clientId,
        fareIds: this.getSelectedFareIds(),
      },
      isCreating: this.isCreating,
      isEditing: this.isEditing,
    });
  }

  async onEditSuccess(updated: ITemporalPass): Promise<void> {
    this.isEditing = false;
    this.isCreating = false;
    if (updated.id) await this.loadFullPass(updated.id);
    else this.editingPass = { ...updated };
  }

  async onCreateSuccess(created: ITemporalPass): Promise<void> {
    this.isCreating = false;
    this.isEditing = false;
    if (created.id) await this.loadFullPass(created.id);
    else this.editingPass = { ...created };
  }

  onDeleteSuccess(): void {
    this.isCreating = false;
    this.isEditing = false;
    this.editingPass = this.getEmpty();
  }

  resetState(): void {
    this.isCreating = false;
    this.isEditing = false;
    this.editingPass = this.getEmpty();
  }

  isFormValid(): boolean {
    if (!this.editingPass.name) return false;
    if (
      this.editingPass.price === undefined ||
      this.editingPass.price === null ||
      this.editingPass.price < 0
    )
      return false;
    if (!this.editingPass.type) return false;
    if (
      this.editingPass.usageLimit === undefined ||
      this.editingPass.usageLimit === null ||
      this.editingPass.usageLimit < 0
    )
      return false;
    return true;
  }
}
